import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";

import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import axiosV2 from "@lhd/libs/axiosV2";
import { User } from "@lhd/types";
import { getAccessToken, getCompanyId } from "@lhd/utility";

export const resolveLoginCompanyId = (
  ...values: Array<string | number | null | undefined>
): number | undefined => {
  for (const value of values) {
    if (value == null || value === "") continue;
    const parsed = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return undefined;
};

export const meQueryKey = (
  companyId = getCompanyId(),
  companyIdParams?: number,
) => [QUERY_NAME.ME, companyId, companyIdParams] as const;

export const meQueryFn = async ({
  queryKey,
}: {
  queryKey: ReturnType<typeof meQueryKey>;
}) => {
  const loginCompanyId = resolveLoginCompanyId(
    queryKey[2],
    queryKey[1],
    getCompanyId(),
  );
  const res = await axios({
    method: "get",
    url: SERVICES.AUTH,
    params:
      loginCompanyId != null ? { login_company_id: loginCompanyId } : undefined,
  });
  return { data: res.data as User };
};

export const useFetchMe = (companyIdParams?: number, callFetchMe = true) => {
  const companyId = getCompanyId();
  const accessToken = getAccessToken();
  const resolvedCompanyIdParam = resolveLoginCompanyId(
    companyIdParams,
    companyId,
  );

  return useQuery({
    queryKey: meQueryKey(companyId, resolvedCompanyIdParam),
    queryFn: meQueryFn,
    cacheTime: 5000,
    staleTime: 5000,
    retry: 2,
    enabled: !!accessToken && callFetchMe,
  });
};

export const useFetchUsers = (
  page: number,
  pageSize: number,
  filterParams: any,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.USERS, companyId, page, pageSize, filterParams],
    () =>
      axiosV2({
        method: "get",
        url: SERVICES.USERS,
        params: {
          page,
          per_page: pageSize,
          ...filterParams,
          company_id: companyId,
          login_company_id: companyId,
        },
      }).then((res) => ({
        data: res.data as User[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: Infinity,
      keepPreviousData: true,
    },
  );
};

export const useFetchOneUser = (userId: any) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.USER, userId, companyId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.USERS}/${userId}`,
        params: { login_company_id: companyId },
      }).then((res) => res.data as User),
    {
      cacheTime: Infinity,
      enabled: !!userId,
    },
  );
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ userId }: any) =>
      axios({
        method: "delete",
        url: `${SERVICES.USERS}/${userId}`,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.USERS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useDeleteMultipleUser = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ listIds, delete_user_options }: any) => {
      const companyId = getCompanyId();

      const formData = new FormData();
      listIds.forEach((id: any) => {
        formData.append(`delete_ids[]`, id);
      });
      if (delete_user_options) {
        formData.append(`delete_user_options`, delete_user_options);
      }
      formData.append("login_company_id", companyId);
      formData.append("company_id", companyId);
      formData.append(`delete_user_options`, delete_user_options);
      return axios({
        method: "post",
        data: formData,
        url: `${SERVICES.USERS}/delete_multiple`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.USERS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useSubmitUser = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, userId }: any) =>
      axios({
        method: userId ? "patch" : "post",
        url: userId ? `${SERVICES.USERS}/${userId}` : SERVICES.USERS,
        data: payload,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.USERS]);
        queryClient.invalidateQueries([QUERY_NAME.USER]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useSubmitSaveUsers = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: SERVICES.USERS_SAVE,
        data: {
          ...payload,
          login_company_id: Number(companyId),
          delete_user_options: "keep_workers_data",
        },
      }),
    {
      onSuccess(e) {
        queryClient.invalidateQueries([QUERY_NAME.USERS]);
        const listErrors = e?.data?.errors?.filter(
          (i: any) => !i?.is_email_existing_in_other,
        );
        if (listErrors?.length > 0) {
          notification["error"]({
            message: listErrors?.[0]?.msg,
          });
        } else {
          // notification["success"]({
          //   message: "保存しました",
          // });
        }
      },
    },
  );
};

export const useSubmitInviteUser = () => {
  return useMutation(
    ({ payload }: any) => {
      return axios({
        method: "post",
        url: `${SERVICES.USERS}${SERVICES.INVITE_TO_COMPANY}`,
        data: { ...payload, company_id: getCompanyId() },
        // headers: {
        //   "Content-Type": "multipart/form-data",
        // },
      });
    },
    {
      onSuccess() {
        // queryClient.invalidateQueries([QUERY_NAME.WORKERS]);
        // queryClient.invalidateQueries([QUERY_NAME.WORKER]);
        // notification["success"]({
        //   message: "保存しました",
        // });
      },
    },
  );
};
